import { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, usePageLayoutStyles } from 'shared-ui';

import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { AppRoute, AppRouteWithParams } from 'routing/AppRoute.enum';
import { AddPositionContainer } from '../positions/addPosition/AddPositionContainer';
import { AddEventExportFormatPageModeEnum } from '../eventsExportFormats/addEventExportFormat/AddEventExportFormat.enum';
import { AddTerminalsDisplayContainer } from '../display/addTerminalsDisplay/AddTerminalsDisplayContainer';
import { useQuery } from 'hooks/useQuery/useQuery';
import { fetchOrganizations } from 'api/organizations/organizations';
import { QueryKeyEnum } from 'core/global.enum';
import { getObjectInNestedObjectByKey } from 'helpers/getObjectInNestedObjectByKey';
import { transformTreeToFlatArray } from 'helpers/transformTreeToFlatArray';
import { OrganizationsTreeSelect } from 'ui/organizationsTreeSelect/OrganizationsTreeSelect';
import { TreeSingleSelect } from 'ui/organizationsTreeSelect/treeSingleSelect/TreeSingleSelect';
import { TableSearchContainer } from 'reusable/tableSearch/TableSearchContainer';
import { AddTerminalsSequenceContainer } from '../sequences/addTerminalsSequence/AddTerminalsSequenceContainer';
import { DefinitionBarRoute, useTopBarRoutes } from '../hooks/useTopBarRoutes';
import { AddPassageContainer } from '../passages/addPassage/AddPassageContainer';

import { DefinitionsBarProps } from './DefinitionsBar.types';
import * as Styled from './DefinitionsBar.styles';

export const DefinitionsBar = ({
  setPositionsTableSearchQuery,
  searchOrganizationId,
  setSearchOrganizationId,
  zones,
  searchZoneId,
  setSearchZoneId,
  addPassageProps,
}: DefinitionsBarProps) => {
  const { formatMessage } = useLocale();
  const location = useLocation();
  const navigate = useNavigate();
  const { routes } = useTopBarRoutes();
  const { headerHeight } = usePageLayoutStyles();

  const { data: organizationResponse } = useQuery([QueryKeyEnum.ORGANIZATIONS], () => fetchOrganizations());

  const organization = organizationResponse ? organizationResponse[organizationResponse.length - 1] : undefined;
  const selectedOrganization =
    organization && searchOrganizationId
      ? getObjectInNestedObjectByKey(organization, 'id', searchOrganizationId)
      : undefined;

  const selectedZone = searchZoneId ? transformTreeToFlatArray(zones).find(({ id }) => id === searchZoneId) : undefined;

  const currentPageBarComponentsDefinition: Record<DefinitionBarRoute['path'], ReactNode> = {
    [AppRoute.definitions]: (
      <OrganizationsTreeSelect
        isSearchType
        useFilteredData={false}
        onChange={(val) => setSearchOrganizationId && setSearchOrganizationId(val?.id)}
        value={selectedOrganization ? selectedOrganization.object : undefined}
        placeholder={formatMessage({ id: AppMessages['definitions.organization.search'] })}
      />
    ),
    [AppRoute.positions]: (
      <>
        {setPositionsTableSearchQuery && (
          <TableSearchContainer
            searchIconTestId="page-search"
            inputPlaceholder={formatMessage({ id: AppMessages['definitions.positions.search'] })}
            searchBy="positions"
            setTableSearchQuery={setPositionsTableSearchQuery}
          />
        )}
        <AddPositionContainer />
      </>
    ),
    [AppRoute.eventsExportFormats]: (
      <Button
        style={{ width: 250 }}
        onClick={() =>
          navigate(AppRouteWithParams.eventsExportFormats.get({ mode: AddEventExportFormatPageModeEnum.ADD }))
        }
        variant="contained"
        color="primary"
      >
        {formatMessage({ id: AppMessages['definitions.events.addExportFormat.title'] })}
      </Button>
    ),
    [AppRoute.ownersControl]: null,
    [AppRoute.display]: <AddTerminalsDisplayContainer />,
    [AppRoute.logotype]: null,
    [AppRoute.sequencesTerminals]: <AddTerminalsSequenceContainer />,
    [AppRoute.passwordPolicy]: null,
    [AppRoute.mailAccount]: null,
    [AppRoute.automation]: null,
    [AppRoute.zones]: (
      <TreeSingleSelect
        isSearchType
        options={zones || []}
        onChange={(zone) => setSearchZoneId && setSearchZoneId(zone?.id)}
        value={selectedZone}
        placeholder={formatMessage({ id: AppMessages['definitions.zones.search'] })}
      />
    ),
    [AppRoute.passages]: addPassageProps ? <AddPassageContainer {...addPassageProps} /> : null,
  };

  const currentPageBarComponents = currentPageBarComponentsDefinition[location.pathname as DefinitionBarRoute['path']];

  const visibleRoutes = Object.values(routes).filter((route) => route.isVisible);

  return (
    <Styled.TopBarWrapper>
      {currentPageBarComponents && (
        <Styled.HeaderActions $headerHeight={headerHeight}>{currentPageBarComponents}</Styled.HeaderActions>
      )}

      <Styled.Chips>
        {visibleRoutes.map((route) => (
          <Styled.Chip key={route.path} to={route.path} $isActive={location.pathname === route.path}>
            {route.label}
          </Styled.Chip>
        ))}
      </Styled.Chips>
    </Styled.TopBarWrapper>
  );
};
